import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Spacing, Text, Title } from '@vkontakte/vkui';
import React, { type RefObject, forwardRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import {
    editingDocumentRestrictionsC,
    editingDocumentRestrictionsQuota,
} from 'reactApp/appHelpers/featuresHelpers/features/editingDocumentRestrictions';
import { EditorRestrictionsActions } from 'reactApp/modules/editorRestrictions/editorRestrictions.module';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { goToPromoQuota } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { EDTIOR_RESTRICTION_SOURCES, getUtmQuery } from 'reactApp/ui/EditorRestriction/EditorRestriction.const';
import { useUserQuota } from 'reactApp/ui/EditorRestriction/hooks/useUserQuota';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './EditorRestrictionTooltip.css';

interface EditorRestrictionTooltipProps {
    onClose(): void;
}

export const EDITOR_RESTRICTION_TOOLTIP_ID = 'editorRestrictionTooltipId';

const sendAnalytics = (action: string) =>
    sendDwh({
        eventCategory: 'tooltip',
        action,
        dwhData: { name: EDTIOR_RESTRICTION_SOURCES.mnz_editor_less_512gb, source: 'desktop', platform: 'web' },
    });

export const EditorRestrictionTooltip = forwardRef<HTMLDivElement, EditorRestrictionTooltipProps>(({ onClose }, ref) => {
    const dispatch = useDispatch();
    const userQuotaValue = useUserQuota();

    const handleShow = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.EDITOR_TOOLTIP_SHOW_512);
        sendAnalytics('show');
        dispatch(UserStorageActions.set(EDITOR_RESTRICTION_TOOLTIP_ID, true));
    }, []);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleBuyClick = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.EDITOR_TOOLTIP_CLICK_512);
        sendAnalytics('click');
        dispatch(EditorRestrictionsActions.suggestTariff({ paysource: EDTIOR_RESTRICTION_SOURCES.editor_less_512gb }));
        onClose();
    }, [onClose]);

    const goToTariffs = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.EDITOR_TOOLTIP_CLICK_TARIFF_512);
        sendAnalytics('click_tariff');
        goToPromoQuota({ currentPage: true, query: getUtmQuery(EDTIOR_RESTRICTION_SOURCES.editor_less_512gb) });
    }, []);

    const tariffQuotaValue = bytesToNDigits(editingDocumentRestrictionsQuota, 3).value;

    const text = editingDocumentRestrictionsC ? 'Добавьте гигабайты за 1 ₽' : 'Добавьте гигабайты';
    const buttonText = editingDocumentRestrictionsC ? `Увеличить место за 1 ₽` : `Увеличить место`;
    return (
        <FloatingTooltip
            onClose={handleClose}
            onShow={handleShow}
            target={ref as RefObject<HTMLDivElement>}
            placement={ETooltipPlacement.bottom}
            qaId="editor-restrictions"
            withinModal
            closable
            closeOnOutsideClick
            usePortal
        >
            <div className={styles.tooltip}>
                <Title className={styles.tooltipTitle}>Доступно с тарифами от {tariffQuotaValue}</Title>
                <Spacing size={4} />
                <Text className={styles.tooltipText}>
                    У вас — {userQuotaValue}. {text} или выберите <a onClick={goToTariffs}>другой тариф</a>
                </Text>
                <Spacing size={12} />
                <Button theme="vk" primary sizeMode={ButtonSizeMode.small} className={styles.tooltipButton} onClick={handleBuyClick}>
                    {buttonText}
                </Button>
            </div>
        </FloatingTooltip>
    );
});

EditorRestrictionTooltip.displayName = 'EditorRestrictionTooltip';
